import { captureException } from 'src/shared/helpers/captureException';

import { useEffect } from 'react';

import { store } from 'src/config/rootStore';
import { Role } from 'src/modules/user/models/user';
import { delay } from 'src/shared/helpers/delay';

export async function fetchInitialData(authenticated: boolean) {
    const { dispatch } = store;

    const fetchTasks: Promise<unknown>[] = [
        retry(dispatch.languages.fetchLanguages),
        retry(dispatch.contentLanguages.fetchContentLanguages),
        retry(dispatch.countries.fetchCountries),
    ];

    if (authenticated) {
        const userInfo = await retry(dispatch.user.fetchUserInfo);

        if (userInfo.role !== Role.COACH) {
            fetchTasks.push(
                retry(dispatch.providers.fetchMyProviders),
                retry(dispatch.goalInterests.fetchGoalInterests),
                retry(dispatch.contentLanguages.fetchContentLanguages),
                retry(dispatch.assessments.fetchAssessments),
                retry(dispatch.assessments.fetchLatestScores),
                retry(dispatch.admin.fetchAdminInfo),
                retry(dispatch.videoSessions.fetchSessions),
            );
        }
    }

    await Promise.allSettled(fetchTasks);
}

export const useFetchInitialData = (authenticated: boolean) => {
    useEffect(() => {
        fetchInitialData(authenticated);
    }, [authenticated]);
};

/**
 * Simple utility to retry an async function multiple times before returning/erroring
 */
async function retry<T>(asyncFn: () => Promise<T>, attempts = 3): Promise<T> {
    let _error: unknown | null = null;

    for (let i = 0; i < attempts; i++) {
        try {
            return await asyncFn();
        } catch (error) {
            // Print more debugging information in tests...
            if (process.env.NODE_ENV === 'test') {
                // eslint-disable-next-line no-console
                console.error(`Retrying ${asyncFn.name ?? 'unknown function'} due to...`, error);
            }

            if (_error) {
                captureException(_error);
            }

            _error = error;
        }

        await delay(100);
    }

    throw _error;
}
